import { ReferralStatus } from '@dermloop/static-values';
import { IsStringDate } from '@dermloop/utils';
import { Exclude } from 'class-transformer';
import {
  IsBoolean,
  IsDate,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  Validate,
} from 'class-validator';
import { BaseCreateDto, BaseDto } from './base.dto';
import { Icd10Dto } from './diagnoses';

export interface InsuranceInfoDto extends Partial<BaseDto> {
  // Insurance information
  firstName?: string;

  lastName?: string;

  patientAddress?: string;

  patientCity?: string;

  patientState?: string;

  patientZip?: string;

  insuranceNumber?: string;

  pcpNetwork?: string;

  insurer?: string;

  payer?: string;

  referralId?: string;

  icd10?: Icd10Dto;

  icd10Code?: string;

  // Health record information
  mrn?: string;

  healthRecordFirstName?: string;

  healthRecordLastName?: string;

  healthRecordBirthDate?: Date;

  // Referral information
  referralStatus?: ReferralStatus;

  hasAssessment: boolean;

  isReleased?: boolean;
}

export class CreateInsuranceInfoDto
  extends BaseCreateDto
  implements Partial<InsuranceInfoDto>
{
  @IsString()
  @IsNotEmpty()
  firstName: string;

  @IsString()
  @IsNotEmpty()
  lastName: string;

  @IsString()
  @IsNotEmpty()
  patientAddress: string;

  @IsString()
  @IsNotEmpty()
  patientCity: string;

  @IsString()
  @IsNotEmpty()
  patientState: string;

  @IsString()
  @IsNotEmpty()
  patientZip: string;

  @IsOptional()
  @IsString()
  insuranceNumber?: string;

  @IsOptional()
  @IsString()
  icd10Code?: string;

  @IsOptional()
  @IsString()
  mrn?: string;

  @Exclude()
  pcpNetwork?: never;

  @IsOptional()
  @IsString()
  insurer?: string;

  @IsString()
  @IsNotEmpty()
  payer: string;

  @IsUUID()
  referralId: string;

  /** Determines if the referral should be released  */
  @IsBoolean()
  releaseReferral: boolean;

  // Health record information
  @IsOptional()
  @IsDate()
  healthRecordBirthDate?: Date;

  @Exclude()
  healthRecordFirstName?: never;

  @Exclude()
  healthRecordLastName?: never;
}

export class UpdateInsuranceInfoDto
  extends BaseCreateDto
  implements Partial<InsuranceInfoDto>
{
  @IsString()
  @IsNotEmpty()
  firstName: string;

  @IsString()
  @IsNotEmpty()
  lastName: string;

  @IsOptional()
  @IsString()
  mrn?: string;

  @IsString()
  @IsNotEmpty()
  patientAddress: string;

  @IsString()
  @IsNotEmpty()
  patientCity: string;

  @IsString()
  @IsNotEmpty()
  patientState: string;

  @IsString()
  @IsNotEmpty()
  patientZip: string;

  @IsOptional()
  @IsString()
  insuranceNumber?: string;

  @IsOptional()
  @IsString()
  icd10Code?: string;

  @Exclude()
  pcpNetwork?: never;

  @IsOptional()
  @IsString()
  insurer?: string;

  @IsString()
  @IsNotEmpty()
  payer: string;

  // Referral id cannot be changed
  @Exclude()
  referralId?: never;

  // Health record information
  @IsOptional()
  @IsDate()
  healthRecordBirthDate?: Date;

  @Exclude()
  healthRecordFirstName?: never;

  @Exclude()
  healthRecordLastName?: never;
}

export class UpdateInsuranceInfoReferralStatusDto {
  // If null, removes the status
  @IsOptional()
  @IsEnum(ReferralStatus)
  status?: ReferralStatus;
}
export type InsuranceInfoReferralStatusType =
  | 'new-referral'
  | 'pending-information'
  | 'released'
  | 'assessed'
  | 'cancelled'
  | 'closed';

export class SearchReferralInsuranceInfoDto {
  @IsNumber()
  @IsOptional()
  @Min(0)
  skip?: number;

  @IsNumber()
  @IsOptional()
  @Min(1)
  @Max(20)
  take?: number;

  @IsOptional()
  @Validate(IsStringDate)
  referralAfterDate?: string;

  @IsOptional()
  @Validate(IsStringDate)
  referralBeforeDate?: string;

  @IsOptional()
  @IsString({ each: true })
  @IsNotEmpty({ each: true })
  status?: InsuranceInfoReferralStatusType[];

  @IsOptional()
  @IsString()
  searchText?: string;

  @IsOptional()
  @IsUUID('all', { each: true })
  medicalUnitIds?: string[];

  @IsOptional()
  @IsUUID('all', { each: true })
  juridistictionalGroupIds?: string[];
}

export class SendFaxDto {
  @IsUUID()
  insuranceInfoId: string;
  @IsUUID()
  referralId: string;
}

class CommonReferralInsuranceInfoDto {
  referralId: string;
  healthRecordId: string;
  conditionId: string;
  /** @deprecated Use conditionId */
  lesionId: string;
  idNumber: number | string;
  // Referral creation date
  created: Date;
  // Referral created by user
  fromUserFirstName: string;
  fromUserLastName: string;
  // Referral created by medical unit
  fromMedicalUnitName: string;
  preferredTeleSpecialistsId: string;
  // Patient info
  patientAccountNumber: string;
  patientFirstName: string;
  patientLastName: string;
  patientBirthDate: string;
  // Null if no insurance info
  insuranceInfoId?: string;
  network?: string;
  jurisdictionalGroupId: string;
  isSuperSeeeded: boolean;
  isReleased: boolean;
  // False if no assessment. Null if not included in query
  hasAssessment?: boolean;
  sendFaxOnReferralClosed: boolean;
  faxSent: boolean;
  // Specialist info
  specialistFirstName?: string;
  specialistLastName?: string;
  specialistGroupMemberId?: string;
  specialistMedicalUnitName?: string;
}

class ClosedReferralInfoDto extends CommonReferralInsuranceInfoDto {
  referralStatus: ReferralStatus.Closed;
  medicalUnitFax: string;
  jurisdictionalGroupFax: string;
}

class NonClosedReferralInfoDto extends CommonReferralInsuranceInfoDto {
  referralStatus: Exclude<ReferralStatus, ReferralStatus.Closed>;
}

export type ReferralInsuranceInfoDto =
  | ClosedReferralInfoDto
  | NonClosedReferralInfoDto;
